/**
 * Node pages, like a wiki or a notes vault (LegendKeeper, Obsidian): every node may have a
 * page of Markdown (`node.page`) or a document (Writing's, `state/documents/`, read here as
 * its Markdown), and `[[Name]]` in a page mentions another node of the same
 * environment by its name (or id, ignoring case). This service knows which page is shown (the
 * node last selected), what each page mentions, which pages mention a node (its backlinks),
 * and follows a link: to its node, or, for a name no node has yet, to the Add node window with
 * that name typed in. Mentions are not links of the graph: they need no kind and break no rule;
 * the graph window may draw them faintly (`mentionLinks`).
 */
import { createEffect, createMemo, createSignal, on, type Accessor } from "solid-js";
import type { Node } from "@protocol/Node";
import type { Actions } from "../client/actions";
import type { GraphMirror } from "../client/graph";
import type { Documents } from "./documents";
import { wikiTargets } from "../shared/markdown";
import type { NodeAdder } from "./nodeAdder";
import type { View } from "./view";

/** A mention between two nodes: `from`'s page names `to`. */
export interface Mention {
  from: string;
  to: string;
}

/** What one graph's pages say, worked out once per change of the graph. */
export interface PageIndex {
  /** Nodes by lower-case name and by lower-case id. */
  byName: Map<string, Node>;
  /** For each node, the nodes its page mentions, in the page's order. */
  mentions: Map<string, string[]>;
  /** For each node, the nodes whose pages mention it, in the graph's order. */
  backlinks: Map<string, string[]>;
  /** For each node, the names its page mentions that no node has. */
  missing: Map<string, string[]>;
}

/** The index of `nodes`' pages (`textOf`: a node's text, when not its `page`: its document's). A node mentioning itself is left out. */
export function indexPages(nodes: readonly Node[], textOf: (node: Node) => string | null = () => null): PageIndex {
  const byName = new Map<string, Node>();
  for (const n of nodes) byName.set(n.id.toLowerCase(), n);
  // A name wins over an id: [[Ann]] is the node named Ann even if another's id is "ann".
  for (const n of nodes) byName.set(n.name.trim().toLowerCase(), n);
  const mentions = new Map<string, string[]>();
  const backlinks = new Map<string, string[]>();
  const missing = new Map<string, string[]>();
  for (const n of nodes) {
    const text = textOf(n) ?? n.page;
    if (!text) continue;
    for (const name of wikiTargets(text)) {
      const to = byName.get(name.toLowerCase());
      if (!to) missing.set(n.id, [...(missing.get(n.id) ?? []), name]);
      else if (to.id !== n.id && !mentions.get(n.id)?.includes(to.id)) {
        mentions.set(n.id, [...(mentions.get(n.id) ?? []), to.id]);
        backlinks.set(to.id, [...(backlinks.get(to.id) ?? []), n.id]);
      }
    }
  }
  return { byName, mentions, backlinks, missing };
}

/** The pages service. */
export interface Pages {
  /** The page shown: the node last selected, while it is in the graph shown. */
  current: Accessor<Node | null>;
  /** The index of the pages of the graph shown. */
  index: Accessor<PageIndex>;
  /** The node a wiki link names (by name or id, ignoring case), if any. */
  resolve(name: string): Node | null;
  /** Every mention of the graph shown (for drawing them). */
  mentionLinks: Accessor<Mention[]>;
  /** Shows the page of node `id` (and selects it). */
  show(id: string): void;
  /** Follows a wiki link: selects its node, or opens Add node with the name when none has it. */
  follow(name: string): void;
  /** Writes `text` as node `id`'s page (one undo); `false` when the core refused it. */
  save(id: string, text: string): Promise<boolean>;
}

/** What the service needs. */
export interface PagesDeps {
  graph: Pick<GraphMirror, "state">;
  view: Pick<View, "state" | "selectNode">;
  actions: Pick<Actions, "updateNode">;
  nodeAdder: Pick<NodeAdder, "open">;
  documents: Pick<Documents, "textOf">;
}

/** Starts the pages service. */
export function createPages({ graph, view, actions, nodeAdder, documents }: PagesDeps): Pages {
  const [shown, setShown] = createSignal<string | null>(null);
  // The page follows the selection; selecting nothing keeps the last page.
  createEffect(
    on(
      () => view.state.selection,
      (s) => {
        if (s?.type === "node") setShown(s.id);
      },
    ),
  );
  const index = createMemo(() => indexPages(graph.state.graph.nodes, (n) => documents.textOf(n)));
  const current = createMemo(() => {
    const id = shown();
    return id ? (graph.state.graph.nodes.find((n) => n.id === id) ?? null) : null;
  });
  const resolve = (name: string) => index().byName.get(name.trim().toLowerCase()) ?? null;
  return {
    current,
    index,
    resolve,
    mentionLinks: createMemo(() => [...index().mentions].flatMap(([from, list]) => list.map((to) => ({ from, to })))),
    show(id) {
      setShown(id);
      view.selectNode(id);
    },
    follow(name) {
      const node = resolve(name);
      if (node) {
        setShown(node.id);
        view.selectNode(node.id);
      } else nodeAdder.open(null, null, null, name.trim());
    },
    async save(id, text) {
      const node = graph.state.graph.nodes.find((n) => n.id === id);
      if (!node || (node.page ?? "") === text) return true;
      return (await actions.updateNode(id, { page: text })) !== null;
    },
  };
}
